'use client';

import { useActionState, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';

export type TermsAcceptanceActionState = {
  error?: 'TERMS_ACCEPTANCE_REQUIRED';
};

export type TermsAcceptanceLabels = {
  title: string;
  officialLanguageNotice?: string;
  version: string;
  effectiveDate: string;
  viewTerms: string;
  requiredSection: string;
  requiredAcceptance: string;
  optionalSection: string;
  optionalNotice: string;
  serviceImprovement: string;
  interpretationAidDevelopment: string;
  trainingAndPracticeImprovement: string;
  acceptAll: string;
  continue: string;
  acceptanceError: string;
};

type Props = {
  locale: string;
  document: {
    version: string;
    effectiveAt: string;
    content: string;
  };
  labels: TermsAcceptanceLabels;
  action: (
    state: TermsAcceptanceActionState,
    formData: FormData
  ) => Promise<TermsAcceptanceActionState>;
};

function LegalDocumentContent({ content }: { content: string }) {
  const lines = content.split('\n');
  const elements: ReactNode[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    if (!line) continue;

    const tableCells = lines[index].split('\t').map((cell) => cell.trim());
    if (tableCells.length === 3) {
      const rows: string[][] = [];
      while (index < lines.length) {
        const cells = lines[index].split('\t').map((cell) => cell.trim());
        if (cells.length !== 3) break;
        rows.push(cells);
        index += 1;
      }
      index -= 1;
      elements.push(
        <div
          key={`table-${index}`}
          role="table"
          className="my-5 overflow-hidden rounded-3 border border-el-grey-300"
        >
          {rows.map((cells, rowIndex) => (
            <div
              key={`${cells[0]}-${rowIndex}`}
              role="row"
              className={`grid gap-px bg-el-grey-300 md:grid-cols-[1.15fr_1fr_0.85fr] ${
                rowIndex === 0 ? 'font-semibold' : ''
              }`}
            >
              {cells.map((cell, cellIndex) => (
                <div
                  key={`${cell}-${cellIndex}`}
                  role={rowIndex === 0 ? 'columnheader' : 'cell'}
                  className={`px-3 py-2.5 ${
                    rowIndex === 0
                      ? 'bg-el-blue-200 text-el-blue-600'
                      : 'bg-white text-el-grey-700'
                  }`}
                >
                  {cell}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
      continue;
    }

    if (index === 0) {
      elements.push(
        <h2
          key={line}
          className="text-22 font-bold leading-tight text-el-grey-900"
        >
          {line}
        </h2>
      );
      continue;
    }
    if (index === 1) {
      elements.push(
        <p key={line} className="mt-1 text-15 font-medium text-el-grey-600">
          {line}
        </p>
      );
      continue;
    }
    if (index === 2) {
      elements.push(
        <p key={line} className="mt-1 text-13 text-el-grey-500">
          {line}
        </p>
      );
      continue;
    }
    if (line === 'Sommaire') {
      elements.push(
        <h2
          key={line}
          className="mb-2 mt-7 border-b border-el-grey-300 pb-2 text-18 font-bold text-el-grey-900"
        >
          {line}
        </h2>
      );
      continue;
    }
    if (/^TITRE [IVX]+\./.test(line)) {
      elements.push(
        <h2
          key={`${line}-${index}`}
          className="mb-3 mt-8 rounded-3 bg-el-blue-200 px-4 py-3 text-17 font-bold text-el-blue-700"
        >
          {line}
        </h2>
      );
      continue;
    }
    if (/^Article \d+\./.test(line)) {
      elements.push(
        <h3
          key={line}
          className="mb-2 mt-6 text-16 font-bold leading-snug text-el-grey-900"
        >
          {line}
        </h3>
      );
      continue;
    }
    if (line.startsWith('•')) {
      elements.push(
        <div
          key={`${line}-${index}`}
          className="flex gap-2 border-b border-el-grey-200 py-1.5 text-13 text-el-grey-600 last:border-0"
        >
          <span aria-hidden="true" className="text-el-blue-500">
            •
          </span>
          <span>{line.replace(/^•\s*/, '')}</span>
        </div>
      );
      continue;
    }

    const letterItem = line.match(/^([a-e]\))\s*(.*)$/);
    if (letterItem) {
      elements.push(
        <div
          key={`${line}-${index}`}
          className="my-2 flex items-start gap-3 pl-3"
        >
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-el-grey-200 text-12 font-semibold text-el-grey-700">
            {letterItem[1]}
          </span>
          <p>{letterItem[2]}</p>
        </div>
      );
      continue;
    }

    const numberedParagraph = line.match(/^(\d+\.\d+\.)\s*(.*)$/);
    if (numberedParagraph) {
      elements.push(
        <div key={`${line}-${index}`} className="my-3 flex items-start gap-3">
          <span className="shrink-0 rounded-2 bg-el-grey-200 px-2 py-0.5 text-12 font-semibold text-el-grey-700">
            {numberedParagraph[1]}
          </span>
          <p>{numberedParagraph[2]}</p>
        </div>
      );
      continue;
    }

    if (line === 'Acceptation') {
      elements.push(
        <h3
          key={line}
          className="mb-2 mt-8 border-t border-el-grey-300 pt-5 text-17 font-bold text-el-grey-900"
        >
          {line}
        </h3>
      );
      continue;
    }
    if (line.startsWith('☐')) {
      elements.push(
        <div
          key={`${line}-${index}`}
          className="my-2 flex items-start gap-3 rounded-3 bg-el-grey-100 px-3 py-2.5"
        >
          <span aria-hidden="true" className="text-el-blue-500">
            ☐
          </span>
          <span>{line.replace(/^☐\s*/, '')}</span>
        </div>
      );
      continue;
    }
    if (
      line.startsWith('Autorisations facultatives') ||
      line.startsWith('Finalité ')
    ) {
      elements.push(
        <p
          key={`${line}-${index}`}
          className="my-3 font-semibold text-el-grey-800"
        >
          {line}
        </p>
      );
      continue;
    }

    elements.push(
      <p key={`${line}-${index}`} className="my-2">
        {line}
      </p>
    );
  }

  return <article className="mx-auto max-w-4xl">{elements}</article>;
}

export function TermsAcceptanceScreen({
  locale,
  document,
  labels,
  action,
}: Props) {
  const [accepted, setAccepted] = useState(false);
  const [optionalChoices, setOptionalChoices] = useState({
    allowServiceImprovement: false,
    allowInterpretationAidDevelopment: false,
    allowTrainingAndPracticeImprovement: false,
  });
  const [state, formAction, pending] = useActionState(action, {});

  const checkbox = (checked: boolean) => (
    <span
      aria-hidden="true"
      className="relative mt-0.5 h-5 w-5 shrink-0 rounded-2 peer-focus-visible:ring-2 peer-focus-visible:ring-el-blue-500 peer-focus-visible:ring-offset-2"
    >
      <span className="block h-5 w-5 rounded-2 border border-el-grey-400 bg-white" />
      {checked ? (
        <span className="absolute inset-0 flex items-center justify-center rounded-2 border border-el-blue-500 bg-el-blue-500 text-14 font-bold leading-none text-white">
          ✓
        </span>
      ) : null}
    </span>
  );

  return (
    <main className="min-h-screen w-full bg-el-grey-200 px-3 pb-8 pt-18 lg:pt-5">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
        <header className="overflow-hidden rounded-4 border border-el-grey-300 bg-white shadow-hover">
          <div className="h-1.5 bg-el-blue-500" />
          <div className="p-4 md:p-5">
            <h1 className="text-24/[90%] font-bold text-el-grey-800">
              {labels.title}
            </h1>
            <div className="mt-3 flex flex-wrap gap-2 text-12 text-el-grey-600">
              <span className="rounded-4 bg-el-blue-200 px-2 py-1 font-medium text-el-blue-600">
                {labels.version} {document.version}
              </span>
              <span className="rounded-4 bg-el-grey-100 px-2 py-1">
                {labels.effectiveDate}:{' '}
                {new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(
                  new Date(document.effectiveAt)
                )}
              </span>
            </div>
            {labels.officialLanguageNotice ? (
              <div className="mt-4 flex items-start gap-3 rounded-4 border border-el-yellow-400 bg-el-yellow-300 p-3 text-14 text-el-grey-800">
                <span
                  aria-hidden="true"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/70 font-bold"
                >
                  i
                </span>
                <p>{labels.officialLanguageNotice}</p>
              </div>
            ) : null}
          </div>
        </header>

        <details className="group overflow-hidden rounded-4 border border-el-grey-300 bg-white shadow-hover">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-4 text-16 font-semibold text-el-grey-800 transition-colors hover:bg-el-grey-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-el-blue-500">
            <span className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-el-blue-200 text-el-blue-600">
                §
              </span>
              {labels.viewTerms}
            </span>
            <span className="text-el-blue-500 transition-transform group-open:rotate-180">
              ↓
            </span>
          </summary>
          <div className="max-h-[62vh] overflow-y-auto border-t border-el-grey-300 bg-el-grey-100 px-4 py-5 text-14 leading-6 text-el-grey-700 md:px-8">
            <LegalDocumentContent content={document.content} />
          </div>
        </details>

        <form action={formAction} className="flex flex-col gap-3">
          <fieldset className="rounded-4 border border-el-blue-300 border-l-4 border-l-el-blue-500 bg-white p-4 shadow-hover md:p-5">
            <legend className="rounded-full bg-el-blue-200 px-3 py-1 text-11 font-bold uppercase tracking-wide text-el-blue-600">
              {labels.requiredSection}
            </legend>
            <label
              className="mt-2 flex cursor-pointer items-start gap-3 rounded-3 p-2 text-15 font-medium text-el-grey-800 transition-colors hover:bg-el-blue-100"
              htmlFor="acceptTerms"
            >
              <input
                id="acceptTerms"
                name="acceptTerms"
                type="checkbox"
                value="true"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
                className="peer sr-only"
              />
              {checkbox(accepted)}
              <span>{labels.requiredAcceptance}</span>
            </label>
          </fieldset>

          <fieldset className="rounded-4 border border-el-grey-300 bg-white p-4 shadow-hover md:p-5">
            <legend className="rounded-full bg-el-grey-200 px-3 py-1 text-11 font-bold uppercase tracking-wide text-el-grey-600">
              {labels.optionalSection}
            </legend>
            <p
              id="optional-notice"
              className="mb-4 mt-2 text-14 text-el-grey-500"
            >
              {labels.optionalNotice}
            </p>
            <div
              className="flex flex-col gap-4"
              aria-describedby="optional-notice"
            >
              {[
                ['allowServiceImprovement', labels.serviceImprovement],
                [
                  'allowInterpretationAidDevelopment',
                  labels.interpretationAidDevelopment,
                ],
                [
                  'allowTrainingAndPracticeImprovement',
                  labels.trainingAndPracticeImprovement,
                ],
              ].map(([name, label]) => (
                <label
                  key={name}
                  className="flex cursor-pointer items-start gap-3 rounded-3 border border-el-grey-200 p-3 text-el-grey-800 transition-colors hover:border-el-blue-300 hover:bg-el-blue-100"
                  htmlFor={name}
                >
                  <input
                    id={name}
                    name={name}
                    type="checkbox"
                    value="true"
                    checked={
                      optionalChoices[name as keyof typeof optionalChoices]
                    }
                    onChange={(event) =>
                      setOptionalChoices((choices) => ({
                        ...choices,
                        [name]: event.target.checked,
                      }))
                    }
                    className="peer sr-only"
                  />
                  {checkbox(
                    optionalChoices[name as keyof typeof optionalChoices]
                  )}
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {state.error === 'TERMS_ACCEPTANCE_REQUIRED' ? (
            <p
              role="alert"
              className="rounded-4 bg-el-red-200 p-3 text-14 text-el-grey-800"
            >
              {labels.acceptanceError}
            </p>
          ) : null}

          <div className="sticky bottom-3 z-10 flex flex-col-reverse gap-3 rounded-4 border border-el-grey-300 bg-white/95 p-3 shadow-hover backdrop-blur-sm sm:flex-row sm:justify-end">
            <button
              type="button"
              disabled={pending}
              onClick={(event) => {
                flushSync(() => {
                  setAccepted(true);
                  setOptionalChoices({
                    allowServiceImprovement: true,
                    allowInterpretationAidDevelopment: true,
                    allowTrainingAndPracticeImprovement: true,
                  });
                });
                event.currentTarget.form?.requestSubmit();
              }}
              className="button-secondary min-w-40 px-4 py-2 font-medium disabled:opacity-50"
            >
              {labels.acceptAll}
            </button>
            <button
              type="submit"
              disabled={!accepted || pending}
              className="button-primary min-w-40 px-4 py-2 font-medium disabled:opacity-50"
            >
              {labels.continue}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
